
// React hooks used for popup state, memoized navigation lists, and sync effects.
import { useCallback, useEffect, useMemo, useState } from "react";

// API call used to load the complete queue item when a popup is opened.
import { getAIGenerationQueueItem } from "api/APIStrichCalls";



const useQueueItemPopup = ({
  token,
  images,
  getQueueItemIdentity,
}) => {

  // Current item shown in popup and its index in completed-images list.
  const [focusImage, setFocusImage] = useState(null);

  const [focusImageIndex, setFocusImageIndex] = useState(null);

  // Job id for the currently focused queue item.
  const focusImageJob = getQueueItemIdentity(focusImage);

  // Completion value changes when an open in-progress job finishes.
  const focusImageCompleted = focusImage?.completed || "";

  // Load the complete queue item whenever the popup moves to a different job or the open job completes.
  useEffect(() => {

    if (!focusImageJob || !token) return undefined;

    let requestCancelled = false;

    // Fetch the full queue item so popup-only data such as bucket children is available.
    const loadFocusImageDetails = async () => {

      try {

        const response = await getAIGenerationQueueItem(token, focusImageJob);

        if (requestCancelled) return;

        // Do not replace the focused item unless a complete queue record was returned.
        if (!response?.success || !Array.isArray(response.results) || response.results.length === 0) {

          console.error("Could not load full AI queue item:", response);

          return;

        }

        const fullQueueItem = response.results[0];

        // Only apply the result if the user is still looking at the same job.
        setFocusImage((currentFocusImage) => {

          if (getQueueItemIdentity(currentFocusImage) !== focusImageJob) return currentFocusImage;

          return { ...currentFocusImage, ...fullQueueItem };

        });

      } catch (error) {

        if (!requestCancelled) console.error("Error loading full AI queue item:", error);

      }

    };

    loadFocusImageDetails();

    // Ignore a late response if the popup closes or moves to another job.
    return () => {

      requestCancelled = true;

    };

  }, [focusImageJob, focusImageCompleted, token, getQueueItemIdentity]);

  // Only images with final image_url can be navigated left/right.
  const completedImages = useMemo(
    () => images.filter((img) => Boolean(img?.image_url)),
    [images]
  );

  // Open popup for queue items. Completed images get left/right navigation.
  const openFocusImage = useCallback((img) => {

    if (!img) return;

    // Completed image: find its index so arrow navigation works.
    if (img.image_url) {

      // Find the index of this image in the completed images list using its identity.
      const targetIdentity = getQueueItemIdentity(img);

      // If identity is missing, fallback to not setting an index (no navigation, but popup still opens).
      const indexInFocusable = completedImages.findIndex(
        (item) => getQueueItemIdentity(item) === targetIdentity
      );

      // Open popup even if index cannot be resolved; navigation just stays disabled.
      setFocusImage(img);

      setFocusImageIndex(indexInFocusable !== -1 ? indexInFocusable : null);

      return;

    }

    // Non-image queue item (in-progress or output-only): open popup with navigation disabled.
    setFocusImage(img);

    setFocusImageIndex(null);

  }, [completedImages, getQueueItemIdentity]);



  // Keep focused item synced with latest queue data as polling updates arrive.
  useEffect(() => {

    if (!focusImageJob || images.length === 0) return;

    // Find the same item in the latest queue header data.
    const latestQueueItem = images.find(
      (item) => getQueueItemIdentity(item) === focusImageJob
    );

    if (!latestQueueItem) return;

    // Merge fresh queue header values without discarding detailed popup-only data.
    setFocusImage((currentFocusImage) => {

      if (getQueueItemIdentity(currentFocusImage) !== focusImageJob) return currentFocusImage;

      if (currentFocusImage === latestQueueItem) return currentFocusImage;

      const updatedFocusImage = { ...currentFocusImage, ...latestQueueItem };

      // Queue polling returns header data only, so keep detailed bucket data already loaded for the popup.
      if (!latestQueueItem.bucket && currentFocusImage.bucket) updatedFocusImage.bucket = currentFocusImage.bucket;

      // Keep detailed workflow data too if it was only present in the full queue-item response.
      if (!latestQueueItem.workflow && currentFocusImage.workflow) updatedFocusImage.workflow = currentFocusImage.workflow;

      return updatedFocusImage;

    });

  }, [focusImageJob, images, getQueueItemIdentity]);



  // Keep focus index in sync when focused completed item list changes.
  useEffect(() => {

    if (!focusImage) return;

    // In-progress item should not have a completed-image index.
    if (!focusImage.image_url) {


      if (focusImageIndex !== null) {

        // Clear focus index to disable navigation if focused item is not a completed image.
        setFocusImageIndex(null);

      }

      return;

    }

    const focusIdentity = getQueueItemIdentity(focusImage);

    if (!focusIdentity) return;

    const resolvedIndex = completedImages.findIndex(
      (item) => getQueueItemIdentity(item) === focusIdentity
    );

    // Update index if item position changed.
    if (resolvedIndex !== -1 && focusImageIndex !== resolvedIndex) {

      setFocusImageIndex(resolvedIndex);

    }
    
  }, [focusImage, focusImageIndex, completedImages, getQueueItemIdentity]);



  // Move popup focus to previous completed image.
  const showPrevImage = useCallback(() => {

    // Is the focus image index valid?
    if (!Number.isInteger(focusImageIndex)) return;

    // Are we at the start of the list already?
    if (focusImageIndex > 0) {

      // Get the previous one
      const prevIndex = focusImageIndex - 1;

      // Set the focus image to the previous image in the completed images list and update the focus index accordingly.
      setFocusImage(completedImages[prevIndex]);

      // Update the focus image index for navigation.
      setFocusImageIndex(prevIndex);

    }

  }, [focusImageIndex, completedImages]);



  // Move popup focus to next completed image.
  const showNextImage = useCallback(() => {

    // Is the focus image index valid?
    if (!Number.isInteger(focusImageIndex)) return;

    // Are we at the end of the list already?
    if (focusImageIndex < completedImages.length - 1) {

      // Get the next one
      const nextIndex = focusImageIndex + 1;

      // Set the focus image to the next image in the completed images list and update the focus index accordingly.
      setFocusImage(completedImages[nextIndex]);

      // Update the focus image index
      setFocusImageIndex(nextIndex);

    }

  }, [focusImageIndex, completedImages]);



  // Keyboard navigation while popup is open.
  useEffect(() => {

    if (!focusImage) return undefined;

    // Handle Keydown events for left/right arrow nav between completed images when popup is open.
    const handleKeyDown = (e) => {

      // Do we have valid image focus to navigate?
      if (!Number.isInteger(focusImageIndex)) return;
      
      // Left/right arrows navigate to previous/next completed image in the list.

      // Arrowleft - call showPrevImage
      if (e.key === "ArrowLeft") showPrevImage();
  
      // ArrowRight - call showNextImage
      if (e.key === "ArrowRight") showNextImage();
    
    };

    // Attach keydown listener when popup is open, and clean up on close or when dependencies change.
    window.addEventListener("keydown", handleKeyDown);

    // Cleanup function to remove event listener when component unmounts or dependencies change.
    return () => window.removeEventListener("keydown", handleKeyDown);

  }, [focusImage, focusImageIndex, showPrevImage, showNextImage]);

  // Popup can navigate only when focused item is a completed image and list has more than one item.
  const canNavigateFocusImage = Boolean(focusImage?.image_url)
    && Number.isInteger(focusImageIndex)
    && completedImages.length > 1;

  return {

    focusImage,
    setFocusImage,
    focusImageIndex,
    completedImages,
    openFocusImage,
    showPrevImage,
    showNextImage,
    canNavigateFocusImage,

  };

};

export default useQueueItemPopup;
